<template>
  <div id="dashboard-custom">
    <!-- Cabecera fija (ícono + texto a la izquierda, botonera a la derecha),
         mismos lineamientos que SimpleModuleDashboard.vue. Acá el ícono y el
         texto están hardcodeados a propósito: esta vista ("Tableros") no es
         un módulo con fila en m_modules_permissions, no hay de dónde leerlos
         — ver docs/tabs-layout. Debajo va el contenido (las tabs con los
         tableros), nunca dentro del <ul> que scrollea, para que el dropdown
         "Panel" no quede clipeado por su overflow. -->
    <div class="dashboard-page-heading">
      <div class="dashboard-page-heading__summary d-none d-md-flex">
        <span class="dashboard-page-heading__icon"><AppIcon name="dashboard" /></span>
        <p>{{ headingText }}</p>
      </div>
      <div class="dashboard-page-heading__actions">
        <span class="dashboard-page-heading__actions-start">
          <TabHeaderActions />
        </span>
        <span v-if="showDashboardActions && !isMobile" class="dashboard-page-heading__actions-end">
          <b-link class="widget-lock-link"
                  :class="{ 'is-locked': isDashboardLocked(dashboardActive) }"
                  @click="toggleDashboardLock(dashboardActive)"
                  v-b-tooltip.hover.bottom
                  :title="isDashboardLocked(dashboardActive) ? 'Dashboard bloqueado. Click para desbloquear' : 'Click para bloquear el dashboard'">
            <AppIcon :name="isDashboardLocked(dashboardActive) ? 'lock-fill' : 'unlock'" />
          </b-link>

          <b-dropdown variant="link" no-caret right boundary="window" toggle-class="widget-config-link" v-b-tooltip.hover.bottom title="Panel">
            <template v-slot:button-content>
              <AppIcon name="dashboard" />
            </template>

            <b-dropdown-item v-if="arr.dashboard.length" @click="openNewDashboard()" title="Agregar Tablero">
              <AppIcon name="add" /> Agregar
            </b-dropdown-item>
            <b-dropdown-item @click="openEditDashboard(dashboardActive)" title="Editar Tablero">
              <AppIcon name="edit" /> Editar
            </b-dropdown-item>
            <b-dropdown-item @click="moveDashboard(dashboardActive, -1)" title="Mover a la izquierda">
              <AppIcon name="back" /> Mover izquierda
            </b-dropdown-item>
            <b-dropdown-item @click="moveDashboard(dashboardActive, 1)" title="Mover a la derecha">
              <AppIcon name="forward" /> Mover derecha
            </b-dropdown-item>
            <b-dropdown-item @click="openImportDashboard()" title="Importar tablero">
              <AppIcon name="download" /> Importar
            </b-dropdown-item>
            <b-dropdown-item @click="openDeleteDashboard(dashboardActive)" title="Eliminar tablero">
              <AppIcon name="delete" /> Eliminar
            </b-dropdown-item>
          </b-dropdown>

          <b-link v-if="!isDashboardLocked(dashboardActive)"
                  class="widget-config-link"
                  @click="openAddWidget(dashboardActive)"
                  v-b-tooltip.hover.bottom
                  title="Agregar Widgets">
            <AppIcon name="calculator" />
          </b-link>
        </span>
      </div>
    </div>

    <b-card no-body v-if="arr.dashboard.length">
      <b-tabs pills card :nav-class="{ 'is-scrollable': tabsScrollable }">
        <b-tab  :title="value.name"
                v-for="(value, key) in arr.dashboard"
                :key="value.id"
                @click="setDashboardActive(value)"
                :active="value.id == tabActive">
        
          <b-card-text>
            <WidgetMasonry
              v-if="getDashboardWidgets(value).length"
              :items="getDashboardWidgets(value)"
              :disabled="isMobile || isDashboardLocked(value)"
              :draggable="!isMobile && !isDashboardLocked(value)"
              :columns="dashboardGridColumns"
              @input="setDashboardWidgets(value, $event)"
              @order-end="onWidgetOrderEnd(value, $event)"
              @resize-end="onWidgetResizeEnd(value, $event)"
            >
              <template v-slot:default="{ item }">
                <Widget
                  :configWidget="getConfigWidget(item)"
                  :reference="item.reference"
                  :hasAccess="getHasAccessWidget(item)"
                  :isCustom="item.php_id > 0"
                  :allowDrag="!isMobile && !isDashboardLocked(value)"
                  :allowResize="!isMobile && !isDashboardLocked(value)"
                  :allowHide="!isDashboardLocked(value)"
                  @hide-widget="hideWidgetFromDashboard(value, item)"
                />
              </template>
            </WidgetMasonry>

            <div v-if="!getDashboardWidgets(value).length" class="dashboard-hero-empty">
              <span class="dashboard-hero-empty__icon"><AppIcon name="calculator" /></span>
              <h3 class="dashboard-hero-empty__title">Este tablero todavía está vacío</h3>
              <p class="dashboard-hero-empty__description">Agregá widgets para empezar a ver la información de tu empresa acá.</p>
              <b-button @click="openAddWidget(value)" variant="dark" class="mt-2">
                <AppIcon name="add" class="mr-1" />Agregar widget
              </b-button>
            </div>

          </b-card-text>

        </b-tab>
      </b-tabs>
    </b-card>
    <div v-else class="dashboard-hero-empty dashboard-hero-empty--home">
      <span class="dashboard-hero-empty__icon"><AppIcon name="dashboard" /></span>
      <h3 class="dashboard-hero-empty__title">Armá tu primer tablero</h3>
      <p class="dashboard-hero-empty__description">Un tablero es un espacio con los widgets que elijas — números, gráficos y accesos rápidos para seguir de un vistazo lo que pasa en tu empresa. Podés crear varios y organizarlos como quieras.</p>
      <b-button @click="openNewDashboard()" variant="dark" size="lg" class="mt-2">
        <AppIcon name="add" class="mr-1" />Crear tablero
      </b-button>
    </div>

    <!-- ########################### -->
    <!-- #####     MODALES     ##### -->
    <!-- ########################### -->

    <!-- CRUD DASHBOARD -->
    <b-modal v-model="modal.form.active">
      <div slot="modal-header" class="app-modal-header">
        <span class="app-modal-header__icon"><AppIcon :name="form.id ? 'edit' : 'add'" /></span>
        <h5 class="app-modal-header__title">{{this.modal.form.title}}</h5>
      </div>

      <b-row>
        <b-col md="12">
          <b-form-group label="Nombre">
            <b-form-input type="text"
                          size="sm"
                          v-model="form.name"
                          required
                          placeholder="Ingresar nombre del rol">
            </b-form-input>
          </b-form-group>
        </b-col>
      </b-row>

      <div slot="modal-footer">
        <b-button variant="outline-secondary" class="mr-1" @click="modal.form.active=false">Cancelar</b-button>
        <b-button variant="dark" @click="saveDashboard()">Guardar</b-button>        
      </div>
    </b-modal>

    <!-- SELECT WIDGET -->
    <b-modal v-model="modal.widget.active"
              no-close-on-esc
              no-close-on-backdrop
              hide-header-close>

      <div slot="modal-header" class="app-modal-header">
        <span class="app-modal-header__icon"><AppIcon name="calculator" /></span>
        <h5 class="app-modal-header__title">{{this.modal.widget.title}}</h5>
      </div>

      <div role="tablist" v-if="arr.widget.length">
        <b-card no-body class="mb-1" v-for="(item, index) in arr.widget" :key="item.id" v-show="validateActiveModule(item)">
          <b-card-header header-tag="header" class="p-1" role="tab">
            <b-button block v-b-toggle="'accordion-' + index" variant="secondary" class="text-left">
              <b>Módulo:</b> {{item.name}}
            </b-button>
          </b-card-header>
          <b-collapse :id="'accordion-'+ index" visible accordion="my-accordion" role="tabpanel">
            <b-card-body>

              <b-list-group>
                <b-list-group-item href="#"
                                   class="flex-column align-items-start"                                                                      
                                   v-for="value in item.widget"
                                   :key="value.id"
                                   v-b-tooltip.hover title="Click para agregar widget"
                                   @click="confirmAddWidget(value)">

                  <div class="d-flex w-100 justify-content-between">
                    <h5 class="mb-1">{{value.title}}</h5>
                    <small>{{value.type}}</small>
                  </div>
                  <p class="mb-1">
                    {{value.description}}
                  </p>

                </b-list-group-item>
              </b-list-group>

            </b-card-body>
          </b-collapse>
        </b-card>
      </div>
      <EmptyState v-else icon="calculator" title="No hay widgets para agregar" />

      <template v-slot:modal-footer>
        <button type="button" class="btn btn-outline-secondary mr-1" @click="cancelAddWidget()">Cancelar</button>
      </template>
    </b-modal>

    <!-- IMPORT DASHBOARD -->
    <b-modal v-model="modal.import.active">
      <div slot="modal-header" class="app-modal-header">
        <span class="app-modal-header__icon"><AppIcon name="download" /></span>
        <h5 class="app-modal-header__title">{{this.modal.import.title}}</h5>
      </div>

      <b-row>
        <b-col md="12">
          <b-form-group label="Usuario">
            <b-form-select v-model="importSelection.user_id" :options="importUserOptions"></b-form-select>
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-form-group label="Tablero">
            <b-form-select v-model="importSelection.dashboard_id" :options="importDashboardOptions" :disabled="!importSelection.user_id"></b-form-select>
          </b-form-group>
        </b-col>
      </b-row>

      <template v-slot:modal-footer>
        <b-button variant="outline-secondary" class="mr-1" @click="modal.import.active=false">Cancelar</b-button>
        <b-button variant="dark" :disabled="!importSelection.dashboard_id" @click="confirmImportDashboard()">Importar</b-button>
      </template>
    </b-modal>

  </div>
</template>
<script>
  import serviceAPI from './services'
  import Error from '@/handler/error'
  import Session from '@/handler/session'
  import Helper from '@/handler/helper'
  import Profiles from '@/config/profiles'
  import Modules from '@/config/modules'
  import Param from '@/config/parameters'
  import Widget from '@/components/inc/widget/widget'
  import WidgetMasonry from '@/components/inc/widget/WidgetMasonry'
  import TabHeaderActions from '@/components/inc/tabs/TabHeaderActions'
  import Funciones from '@/handler/funciones'
  import Storage from '@/handler/storageLocal'
  import { EventBus } from '@/handler/event-bus'

  export default {
    components: {
      Widget,
      WidgetMasonry,
      TabHeaderActions
    },
    props: {
      profileID: {
        type: Number,
        required: true
      },
    },
    data: () => {
      return {
        configWidget: {
          module_id: 0,
          profile_id: 0,
          elements: {}
        },
        form: {
          id: 0,
          name: '',
          columns: 0,
          profile_id: 0,
        },
        modal: {
          form: {
            active: false,
            title: ''
          },
          widget: {
            active: false,
            title: ''
          },
          import: {
            active: false,
            title: ''
          }
        },
        arr: {
          dashboard: [],
          widget: []
        },
        dashboardWidgetsMap: {},
        dashboardSelection: {},
        dashboardActive: {},
        dashboardGridColumns: 12,
        isMobile: false,
        importUsers: [],
        importSelection: {
          user_id: null,
          dashboard_id: null
        },
        tabActive: 0,
        tabsScrollable: false,
        headingText: 'Widgets y métricas de tu empresa, organizados como quieras',
      }
    },
    computed: {
      showDashboardActions() {
        return !!(this.arr.dashboard.length && this.dashboardActive && this.dashboardActive.id)
      },
      importUserOptions() {
        var options = [{ value: null, text: 'Seleccionar usuario' }]
        if(Array.isArray(this.importUsers)) {
          this.importUsers.forEach((user) => {
            options.push({
              value: user.id,
              text: user.name + (user.email ? ' (' + user.email + ')' : '')
            })
          })
        }
        return options
      },
      importDashboardOptions() {
        var options = [{ value: null, text: 'Seleccionar tablero' }]
        var user = this.importUsers.find(item => item.id === this.importSelection.user_id)
        if(user && Array.isArray(user.dashboards)) {
          user.dashboards.forEach((dashboard) => {
            options.push({
              value: dashboard.id,
              text: dashboard.name
            })
          })
        }
        return options
      }
    },
    created () {
      this.configWidget.profile_id = this.profileID
      this.form.profile_id = this.profileID

      window.addEventListener('resize', this.showColumnsDashboard);

      // Título e ícono hardcodeados a propósito: esta vista no tiene fila en
      // m_modules_permissions (no es un módulo), así que no hay de dónde
      // resolverlos como en el resto de las pestañas — ver docs/tabs-layout.
      this.announceTabMetadata()
    },
    // Con <keep-alive> (DefaultTabsLayout.vue), volver a esta vista ya
    // visitada dispara activated() en vez de created() — hay que
    // reanunciarse igual para que la pestaña vuelva a tener el ícono.
    activated() {
      this.announceTabMetadata()
    },
    mounted() {
      this.restoreDashboardFromRoute()
      this.load()

      this.restoreStatusTabsStorage()
    },
    destroyed() {
      window.removeEventListener("resize", this.showColumnsDashboard);
    },          
    watch: {
      '$route.query.dashboard'() {
        this.restoreDashboardFromRoute()
      }
    },
    methods: {
      confirmDialog(message, options) {
        var danger = !!options.danger
        var icon = options.icon || (danger ? 'warning' : 'help')
        var title = this.$createElement('span', { class: 'app-confirm-header__title' }, [
          this.$createElement('AppIcon', { props: { name: icon } }),
          options.title
        ])
        return this.$bvModal.msgBoxConfirm(message, {
          title: title,
          size: 'sm',
          buttonSize: 'sm',
          okVariant: danger ? 'danger' : 'dark',
          okTitle: options.okTitle,
          cancelTitle: options.cancelTitle || 'Cancelar',
          footerClass: 'p-2',
          headerClass: danger ? 'app-confirm-header app-confirm-header--danger' : 'app-confirm-header',
          hideHeaderClose: false,
          centered: true
        })
      },
      load () {
        this.arr.dashboard = []
        this.arr.widget = []
        this.dashboardWidgetsMap = {}

        this.loadDashboard()
        this.loadWidget()
      },
      loadDashboard () {
        this.arr.dashboard = Session.getSession().auth.user.dashboard
        if(this.arr.dashboard.length) {
          var storageTab = Storage.getValue('tabs_view_dashboard')
          var storedId = storageTab ? JSON.parse(storageTab) : 0
          var routeDashboardId = this.$route.query.dashboard
          var preferredId = routeDashboardId || this.tabActive || (this.dashboardActive && this.dashboardActive.id) || storedId
          var selected = this.arr.dashboard.find(item => item.id == preferredId)
          this.dashboardActive = selected || this.arr.dashboard[0]
          this.tabActive = this.dashboardActive.id
          this.showColumnsDashboard()
        }
      },
      restoreDashboardFromRoute() {
        var dashboardId = this.$route.query.dashboard
        if (!dashboardId) return

        this.tabActive = dashboardId
        var dashboard = this.arr.dashboard.find(item => item.id == dashboardId)
        if (dashboard) this.setDashboardActive(dashboard)
      },
      loadWidget () {
        var arrWidget = Session.getSession().auth.user.permissions.widget
        var modules = []
        var widget = []
        var module_name = ''

        for (const prop in arrWidget) {
          var moduleName = (arrWidget[prop].php_id && arrWidget[prop].php_id > 0)
            ? 'php'
            : (arrWidget[prop].module && arrWidget[prop].module.name ? arrWidget[prop].module.name : 'Personal')
          modules[moduleName] = {}
          widget[moduleName] = []
        }

        for (const prop in arrWidget) {
          var moduleName = (arrWidget[prop].php_id && arrWidget[prop].php_id > 0)
            ? 'php'
            : (arrWidget[prop].module && arrWidget[prop].module.name ? arrWidget[prop].module.name : 'Personal')
          var moduleId = arrWidget[prop].module && arrWidget[prop].module.id ? arrWidget[prop].module.id : 1
          if(module_name != moduleName) {

            modules[moduleName] = {  id: moduleId, name: moduleName,}
            modules[moduleName]["widget"] = widget[moduleName]

            module_name = moduleName
          }

          widget[moduleName].push(arrWidget[prop])
        }

        for (const prop in modules) {
          this.arr.widget.push(modules[prop])
        }
      },
      openNewDashboard () {
        this.form.id = 0
        this.form.name = ''

        this.modal.form.title = "Crear Nuevo Tablero"
        this.modal.form.active = true
      },
      openEditDashboard (value) {
        this.form.id = value.id
        this.form.name = value.name

        this.modal.form.title = "Editar Tablero --> " + value.name
        this.modal.form.active = true
      },
      openDeleteDashboard (value) {
        this.form.id = value.id
        this.form.name = value.name

        this.confirmDialog('¿Desea borrar el tablero (' + this.form.name + ') y todos los widgets configurados?', {
          title: 'Borrar Tablero',
          icon: 'delete',
          danger: true,
          okTitle: 'SI',
          cancelTitle: 'NO'
        })
        .then(value => {
          if (value) {
            this.deleteDashboard()
          }
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error));
        })
      },
      saveDashboard () {
        let loader = this.$loading.show();
        if(this.form.id) {
          var result = serviceAPI.editar(this.form);
        } else {
          var result = serviceAPI.agregar(this.form);
        }

        result.then((response) => {
          var result_1 = serviceAPI.getSessionUser();
          result_1.then((response_1) => {
            var data = response_1.data

            Session.setSessionAuth(data)
            this.modal.form.active = false
            this.load()
            loader.hide()
            this.$awn.success("Tablero guardado");
          })
          .catch(error_1 => {
            loader.hide()
            this.$awn.alert(Error.showError(error_1))
          })
        })
        .catch(error => {
          loader.hide()
          this.$awn.alert(Error.showError(error));
        })
      },
      deleteDashboard () {
        let loader = this.$loading.show();
        if (this.form.id) {
          var result = serviceAPI.eliminar(this.form.id);
        }

        result.then((response) => {
          var result_1 = serviceAPI.getSessionUser();
          result_1.then((response_1) => {
            var data = response_1.data

            Session.setSessionAuth(data)
            this.modal.form.active = false
            this.load()
            loader.hide()
            this.$awn.success("Tablero eliminado");
          })
          .catch(error_1 => {
            loader.hide()
            this.$awn.alert(Error.showError(error_1))
          })
        })
        .catch(error => {
          loader.hide()
          this.$awn.alert(Error.showError(error));
        })
      },
      setDashboardActive(dashboard) {
        this.dashboardActive = dashboard
        this.tabActive = dashboard && dashboard.id ? dashboard.id : 0
        this.showColumnsDashboard()

        this.saveStatusTabsStorage()
      },
      showColumnsDashboard() {
        this.isMobile = window.innerWidth <= 992
        this.dashboardGridColumns = 12
        this.checkTabsOverflow()
      },
      announceTabMetadata() {
        EventBus.$emit('Update:TabMetadata', { title: 'Tableros', subtitle: this.headingText, icon: 'dashboard' })
      },
      // El scroll horizontal de las tabs de tableros (overflow-x: auto) ya se
      // activa solo cuando corresponde; esto controla únicamente las
      // flechitas ‹ › decorativas (.is-scrollable), que no deben aparecer si
      // en realidad no hay overflow.
      checkTabsOverflow() {
        this.$nextTick(() => {
          var el = this.$el && this.$el.querySelector('.card-header-pills')
          this.tabsScrollable = !!(el && el.scrollWidth > el.clientWidth + 1)
        })
      },
      openAddWidget (value) {
        var targetDashboard = value || this.dashboardActive || this.arr.dashboard.find(item => item.id == this.tabActive)
        if(!targetDashboard || !targetDashboard.id) {
          return
        }
        if(this.isDashboardLocked(targetDashboard)) {
          this.toggleDashboardLock(targetDashboard).then(() => {
            var refreshed = this.arr.dashboard.find(item => item.id == targetDashboard.id)
            if(refreshed && !this.isDashboardLocked(refreshed)) {
              this.openAddWidget(refreshed)
            }
          })
          return
        }
        this.modal.widget.title = "Agregar Widgets"
        this.modal.widget.active = true

        this.dashboardSelection = targetDashboard
      },
      confirmAddWidget (item) {
        this.confirmDialog('¿Agregar el widget (' + item.title + ') a este tablero?', {
          title: 'Agregar Widget',
          icon: 'add',
          okTitle: 'SI',
          cancelTitle: 'NO'
        })
        .then(value => {
          if (value) {
            this.addWidget(item)
          }
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error));
        })
      },
      cancelAddWidget (){
        this.modal.widget.active = false
        this.dashboardSelection = {}
      },
      addWidget (item) {
        let loader = this.$loading.show();

        var arrWidget = this.getDashboardWidgets(this.dashboardSelection).slice()
        arrWidget.push(item)

        var data = {
          id: this.dashboardSelection.id,
          data: arrWidget,
          type: 'add'
        }

        var result = serviceAPI.actualizarContenidoDashboard(data);

        result.then((response) => {
           var result_1 = serviceAPI.getSessionUser();
          result_1.then((response_1) => {
            var data = response_1.data

            Session.setSessionAuth(data)
            this.modal.widget.active = false
            this.load()
            loader.hide()
            this.$awn.success("Dashboard actualizado");
          })
          .catch(error_1 => {
            loader.hide()
            this.$awn.alert(Error.showError(error_1))
          })
        })
        .catch(error => {
          loader.hide()
          this.$awn.alert(Error.showError(error));
        })
      },
      getConfigWidget(item){
        var data = {
          module_id: item.php_id > 0 ? Modules.PHP : item.module_id,
          profile_id: this.profileID,
        }
        return data
      },
      getHasAccessWidget(item) {
        if(item.php_id > 0) {
          return Helper.hasAccessWidget({ module_id: Modules.PERSONAL, profile_id: this.profileID }, item.reference)
        }
        return Helper.hasAccessWidget(this.getConfigWidget(item), item.reference)
      },
      isDashboardLocked(dashboard) {
        if(!dashboard) {
          return false
        }
        const value = dashboard.locked
        return value === true || value === 1 || value === '1'
      },
      toggleDashboardLock(dashboard) {
        if(!dashboard || !dashboard.id) {
          return Promise.resolve()
        }
        var locked = !this.isDashboardLocked(dashboard)
        return serviceAPI.actualizarLockDashboard(dashboard.id, locked)
          .then(() => serviceAPI.getSessionUser())
          .then((response) => {
            Session.setSessionAuth(response.data)
            this.load()
          })
          .catch((error) => {
            this.$awn.alert(Error.showError(error))
          })
      },
      validateActiveModule(item) {
        if(item.id === Modules.PHP && Helper.hasParametersAccess(Param.P70)) {
          return true
        }
        return Helper.moduleEnabled(item.id)
      },

      getDashboardWidgets(dashboard) {
        if(!dashboard || !dashboard.id) {
          return []
        }
        if(this.dashboardWidgetsMap[dashboard.id]) {
          return this.dashboardWidgetsMap[dashboard.id]
        }

        var parsed = []
        if(dashboard.data) {
          try {
            parsed = JSON.parse(dashboard.data)
          } catch (error) {
            parsed = []
          }
        }

        this.$set(this.dashboardWidgetsMap, dashboard.id, parsed)
        return parsed
      },
      setDashboardWidgets(dashboard, widgets) {
        if(!dashboard || !dashboard.id) {
          return
        }
        this.$set(this.dashboardWidgetsMap, dashboard.id, widgets || [])
      },
      onWidgetOrderEnd(dashboard, widgets) {
        if(this.isMobile) {
          return
        }
        this.setDashboardWidgets(dashboard, widgets)
        this.persistDashboardWidgets(dashboard, widgets)
      },
      onWidgetResizeEnd(dashboard, payload) {
        if(this.isMobile) {
          return
        }
        var list = this.getDashboardWidgets(dashboard).slice()
        var index = list.findIndex(item => item.reference === payload.reference)
        if(index === -1) {
          return
        }
        var current = list[index]
        var next = Object.assign({}, current)
        if(payload.cols !== undefined) {
          next.cols = payload.cols
        }
        if(payload.height !== undefined) {
          next.height = payload.height
        }
        list.splice(index, 1, next)
        this.setDashboardWidgets(dashboard, list)
        this.persistDashboardWidgets(dashboard, list)
      },
      hideWidgetFromDashboard(dashboard, item) {
        if(!item || !dashboard) {
          return
        }
        var list = this.getDashboardWidgets(dashboard).slice()
        var index = list.findIndex(entry => entry.reference === item.reference)
        if(index === -1) {
          return
        }
        list.splice(index, 1)
        this.setDashboardWidgets(dashboard, list)
        this.persistDashboardWidgets(dashboard, list)
      },
      persistDashboardWidgets(dashboard, widgets) {
        var data = {
          id: dashboard.id,
          data: widgets,
          type: 'config'
        }
        serviceAPI.actualizarContenidoDashboard(data)
          .then(() => serviceAPI.getSessionUser())
          .then((response) => {
            Session.setSessionAuth(response.data)
            this.load()
          })
          .catch((error) => {
            this.$awn.alert(Error.showError(error))
          })
      },

      // TABS SAVE
      saveStatusTabsStorage() {
        if(this.dashboardActive && this.dashboardActive.id) {
          Storage.setValue('tabs_view_dashboard', JSON.stringify(this.dashboardActive.id))
        }
      },
      restoreStatusTabsStorage() {
        if(this.$route.query.dashboard) {
          return
        }
        if(Storage.getValue('tabs_view_dashboard')) {
          this.tabActive = JSON.parse(Storage.getValue('tabs_view_dashboard'))

          this.arr.dashboard.forEach((element)=> {
            if(element.id == this.tabActive) {
              this.dashboardActive = element
            }            
          })         
          this.showColumnsDashboard()          
        }         
      },
      moveDashboard(dashboard, direction) {
        if(this.isMobile) {
          return
        }
        var index = this.arr.dashboard.findIndex(item => item.id === dashboard.id)
        if(index === -1) {
          return
        }
        var nextIndex = index + direction
        if(nextIndex < 0 || nextIndex >= this.arr.dashboard.length) {
          return
        }
        var list = this.arr.dashboard.slice()
        var temp = list[index]
        list[index] = list[nextIndex]
        list[nextIndex] = temp
        this.arr.dashboard = list
        this.persistDashboardOrder()
      },
      persistDashboardOrder() {
        var order = this.arr.dashboard.map(item => item.id)
        serviceAPI.actualizarOrdenDashboard({ order: order })
          .then(() => serviceAPI.getSessionUser())
          .then((response) => {
            Session.setSessionAuth(response.data)
            this.load()
          })
          .catch((error) => {
            this.$awn.alert(Error.showError(error))
          })
      },
      openImportDashboard() {
        this.modal.import.title = "Importar Tablero"
        this.modal.import.active = true
        this.importSelection.user_id = null
        this.importSelection.dashboard_id = null
        this.loadImportOptions()
      },
      loadImportOptions() {
        serviceAPI.obtenerOpcionesImportacion()
          .then((response) => {
            this.importUsers = response.data || []
          })
          .catch((error) => {
            this.importUsers = []
            this.$awn.alert(Error.showError(error))
          })
      },
      confirmImportDashboard() {
        if(!this.importSelection.dashboard_id) {
          return
        }
        let loader = this.$loading.show();
        serviceAPI.importarDashboard({ dashboard_id: this.importSelection.dashboard_id })
          .then(() => serviceAPI.getSessionUser())
          .then((response) => {
            Session.setSessionAuth(response.data)
            this.modal.import.active = false
            this.load()
            loader.hide()
            this.$awn.success("Tablero importado");
          })
          .catch((error) => {
            loader.hide()
            this.$awn.alert(Error.showError(error))
          })
      },               
    }
  }
</script>
<style>
  #dashboard-custom > .card > .card-header {
    padding: 6px 10px !important;
  }
  /* Tabs de tableros: siempre en una sola fila con scroll horizontal (nunca
     se apilan verticalmente aunque haya muchos). Las acciones del tablero
     viven en .dashboard-page-heading, fuera de este <ul> — si compartieran
     este contenedor, el dropdown "Panel" quedaría clipeado por el overflow. */
  #dashboard-custom .card-header-pills {
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    margin-bottom: 0;
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    white-space: nowrap;
    position: relative;
  }
  #dashboard-custom .card-header-pills::-webkit-scrollbar {
    display: none;
  }
  #dashboard-custom .card-header-pills > .nav-item {
    flex: 0 0 auto;
    max-width: none;
  }
  /* ── Pill de cada tablero: activo vs. inactivo bien diferenciados ── */
  #dashboard-custom .card-header-pills .nav-link {
    padding: 6px 14px;
    border-radius: 20px;
    color: var(--app-color-text-secondary);
    background: transparent;
    font-weight: var(--app-font-medium);
    transition: background-color 0.15s ease, color 0.15s ease;
  }
  #dashboard-custom .card-header-pills .nav-link:hover:not(.active) {
    color: var(--app-color-primary);
    background: var(--app-color-primary-soft);
  }
  #dashboard-custom .card-header-pills .nav-link.active {
    color: var(--app-color-on-primary);
    background: var(--app-color-primary);
    font-weight: var(--app-font-semibold);
    box-shadow: var(--app-shadow-sm);
  }
  /* Flechitas ‹ › decorativas: solo si .is-scrollable (overflow real,
     calculado en JS por checkTabsOverflow) — sin overflow no deben verse. */
  #dashboard-custom .card-header-pills.is-scrollable::before,
  #dashboard-custom .card-header-pills.is-scrollable::after {
    position: sticky;
    top: 0;
    z-index: 3;
    display: inline-flex;
    align-items: center;
    height: 100%;
    min-width: 20px;
    justify-content: center;
    padding: 0 6px;
    font-size: 18px;
    font-weight: 700;
    color: var(--app-color-text-secondary);
    pointer-events: none;
    text-shadow: 0 0 2px var(--app-color-surface);
  }
  #dashboard-custom .card-header-pills.is-scrollable::before {
    content: "‹";
    left: 0;
    background: linear-gradient(to right, var(--app-color-surface-subtle) 60%, transparent);
  }
  #dashboard-custom .card-header-pills.is-scrollable::after {
    content: "›";
    right: 0;
    background: linear-gradient(to left, var(--app-color-surface-subtle) 60%, transparent);
  }
  .dashboard-hero-empty {
    z-index: 1000;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: 56px 24px;
    border: 1px dashed var(--app-color-border-strong);
    border-radius: 12px;
    background: var(--app-color-surface-subtle);
  }
  .dashboard-hero-empty--home {
    padding: 88px 24px;
  }
  .dashboard-hero-empty__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 76px;
    height: 76px;
    margin-bottom: 20px;
    border-radius: 20px;
    background: var(--app-color-primary-soft);
    color: var(--app-color-primary);
    font-size: 34px;
  }
  .dashboard-hero-empty--home .dashboard-hero-empty__icon {
    width: 96px;
    height: 96px;
    font-size: 44px;
  }
  .dashboard-hero-empty__title {
    margin: 0 0 8px;
    font-size: 20px;
    font-weight: var(--app-font-semibold);
    color: var(--app-color-text);
  }
  .dashboard-hero-empty--home .dashboard-hero-empty__title {
    font-size: 26px;
  }
  .dashboard-hero-empty__description {
    max-width: 480px;
    margin: 0;
    color: var(--app-color-text-secondary);
    font-size: 14px;
    line-height: 1.5;
  }
  .dashboard-hero-empty--home .dashboard-hero-empty__description {
    max-width: 560px;
    font-size: 15px;
  }
  #dashboard-custom canvas {
    min-height: 400px;
  }

  /* ── Cabecera de acciones (mismo lenguaje que SimpleModuleDashboard.vue) ── */
  .dashboard-page-heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin: 0 0 12px;
  }
  .dashboard-page-heading__summary {
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: 30px;
  }
  .dashboard-page-heading__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 8px;
    background: var(--app-color-primary-soft);
    color: var(--app-color-primary);
    font-size: 0.85rem;
  }
  .dashboard-page-heading p {
    margin: 0;
    color: var(--app-color-text-secondary);
    font-size: var(--app-text-sm);
    font-weight: var(--app-font-medium);
  }
  .dashboard-page-heading__actions {
    display: flex;
    align-items: center;
    min-height: 36px;
    padding: 3px 5px;
    border: 1px solid var(--app-color-border);
    border-radius: 9px;
    background: var(--app-color-surface);
    box-shadow: var(--app-shadow-sm);
  }
  .dashboard-page-heading__actions-start,
  .dashboard-page-heading__actions-end {
    display: flex;
    align-items: center;
  }
  .dashboard-page-heading__actions .widget-config-link,
  .dashboard-page-heading__actions .widget-lock-link {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 27px;
    height: 27px;
    margin-right: 2px !important;
    padding: 0 !important;
    border-radius: 6px;
    /* .btn-link de Bootstrap (variant="link" del dropdown "Panel") le gana
       por orden de cascada al color de este token si no se fuerza acá. */
    color: var(--app-color-icon) !important;
  }
  .dashboard-page-heading__actions .widget-lock-link {
    color: var(--app-color-danger) !important;
  }
  .dashboard-page-heading__actions .widget-lock-link.is-locked {
    color: var(--app-color-text-secondary) !important;
  }
  .dashboard-page-heading__actions .widget-config-link:last-child,
  .dashboard-page-heading__actions .widget-lock-link:last-child {
    margin-right: 0 !important;
  }
  .dashboard-page-heading__actions .widget-config-link:hover,
  .dashboard-page-heading__actions .widget-lock-link:hover {
    background: var(--app-color-primary-soft);
  }
  @media (max-width: 767.98px) {
    .dashboard-page-heading__actions {
      width: 100%;
      justify-content: space-between;
      min-height: 44px;
      padding: 4px 6px;
      border-radius: 10px;
    }
    .dashboard-page-heading__actions .widget-config-link,
    .dashboard-page-heading__actions .widget-lock-link {
      width: 34px;
      height: 34px;
      font-size: 18px;
    }
  }
  /* Acá se pidió una botonera más grande que el estándar de arriba (27/34px,
     el mismo que usa SimpleModuleDashboard.vue y el resto de los dashboards
     ya migrados) — se escala con el prefijo #dashboard-custom para no tocar
     ese contrato compartido en las otras vistas que reusan estas mismas
     clases globales. Incluye .tab-header-actions__btn (pin/estrella) para
     que toda la botonera crezca pareja. */
  #dashboard-custom .dashboard-page-heading__actions {
    min-height: 40px;
    padding: 4px 6px;
    border-radius: 10px;
  }
  #dashboard-custom .dashboard-page-heading__actions .widget-config-link,
  #dashboard-custom .dashboard-page-heading__actions .widget-lock-link,
  #dashboard-custom .dashboard-page-heading__actions .tab-header-actions__btn {
    width: 32px;
    height: 32px;
    font-size: 18px;
  }
  @media (max-width: 767.98px) {
    #dashboard-custom .dashboard-page-heading__actions {
      min-height: 50px;
      padding: 5px 7px;
    }
    #dashboard-custom .dashboard-page-heading__actions .widget-config-link,
    #dashboard-custom .dashboard-page-heading__actions .widget-lock-link,
    #dashboard-custom .dashboard-page-heading__actions .tab-header-actions__btn {
      width: 38px;
      height: 38px;
      font-size: 20px;
    }
  }

  /* ── Íconos de acción (mismas clases globales que WidgetVisibilityConfig.vue) ── */
  .widget-config-link {
    font-size: 16px;
    color: var(--app-color-icon);
    cursor: pointer;
  }
  .widget-config-link:hover {
    color: var(--app-color-primary);
  }
  .widget-lock-link {
    font-size: 16px;
    color: var(--app-color-danger);
    cursor: pointer;
    animation: blink-lock 1.5s ease-in-out infinite;
  }
  .widget-lock-link:hover {
    color: var(--app-color-danger-hover);
  }
  .widget-lock-link.is-locked {
    color: var(--app-color-text-secondary);
    animation: none;
  }
  .widget-lock-link.is-locked:hover {
    color: var(--app-color-text-muted);
  }
  @keyframes blink-lock {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.3; }
  }
</style>
